import { getApiUrl } from "src/common/api/getUrl";
import { dataParser } from "src/common/dataParser";
import { mdmLoader } from "src/common/mdmLoader";
import { rest } from "src/common/rest";
import { RtLoaderFn } from "src/components/ReactiveTable/RtLoader";
import z from "zod";

import { ZSpecificationTyped, zSpecificationTyped } from "../specification";
import { CatalogFilters, ZCatalog, zCatalog } from "./ZCatalog";
import { ZCatalogUpdate } from "./ZCatalogUpdate";

const urlCatalog = (path: string = "") => getApiUrl(`/core/catalogs${path}`);

export const loadCatalog = async (id: string): Promise<ZCatalog> => {
  const resp = await rest.get(urlCatalog(`/${id}`));
  return dataParser(() => zCatalog.parse(resp.data), "loadCatalog");
};

export const catalogsLoader = (): RtLoaderFn<ZCatalog, CatalogFilters> =>
  mdmLoader(zCatalog, urlCatalog());

export const createCatalog = async (
  data: ZCatalogUpdate,
): Promise<ZCatalog> => {
  const resp = await rest.post(urlCatalog(), data);
  return dataParser(() => zCatalog.parse(resp.data), "createCatalog");
};

export const updateCatalog = async (
  catalogId: string,
  data: ZCatalogUpdate,
): Promise<ZCatalog> => {
  const resp = await rest.put(urlCatalog(`/${catalogId}`), data);
  return dataParser(() => zCatalog.parse(resp.data), "updateCatalog");
};

export const deleteCatalog = async (catalogId: string) => {
  await rest.delete(urlCatalog(`/${catalogId}`));
};

export const loadCatalogSchemes = async (
  catalogId: string,
): Promise<ZSpecificationTyped[]> => {
  const resp = await rest.get(urlCatalog(`/${catalogId}/schemas`));
  return zSpecificationTyped.array().parse(resp.data);
};
export const loadCatalogSchemesAll = async (
  catalogId: string,
): Promise<ZSpecificationTyped[]> => {
  const resp = await rest.get(urlCatalog(`/${catalogId}/schemas/all`));
  return zSpecificationTyped.array().parse(resp.data);
};

export const loadCatalogSchemesByClasses = async (
  catalogId: string,
  classIds: string[],
): Promise<ZSpecificationTyped[]> => {
  const resp = await rest.post(urlCatalog(`/${catalogId}/schemas`), {
    classIds,
  });
  return zSpecificationTyped.array().parse(resp.data);
};

export const availableCatalogsForTranslationLoader =
  (
    classId: string,
    secondSchemaId: string,
  ): RtLoaderFn<ZCatalog, CatalogFilters> =>
  async (request) => {
    const url = urlCatalog(`/available-for-translation/${classId}`);
    const params = {
      ...request.filters,
      secondSchemaId,
      page: request.page,
      size: request.pageSize,
    };
    const zResult = z.object({
      content: zCatalog.array(),
      pagination: z.object({
        totalElements: z.number(),
      }),
    });
    const resp = await rest.get(url, { params });
    const { content, pagination } = dataParser(
      () => zResult.parse(resp.data),
      url,
    );
    return {
      rows: content,
      totalRows: pagination.totalElements,
    };
  };
